import { useMemo } from 'react';
import { useToast } from '@givebutter/primitives/toast';
import { faLink } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import * as RequiredItem from 'components/required-checklist/required-items/RequiredItem';
import { useChecklistData } from 'components/required-checklist/useChecklistData';
import { useCopy } from 'components/snippet';
import { posthogEvents } from 'constants/posthog-events';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { usePosthog } from 'customHooks/usePosthog';

const RequiredFirstDollar = () => {
    const { pushEvent } = usePosthog();
    const { recentCampaigns } = useCurrentAccount();
    const { checklist } = useChecklistData();
    const { raiseYourFirstDollar, createCampaign, connectPayoutAccount } = checklist;
    const { success } = useToast();

    const { onClickCopy } = useCopy({
        onCopy: () => success('Campaign link copied!'),
    });

    const hasCreatedCampaign = recentCampaigns.length > 0 && createCampaign;

    const buttonText = useMemo(() => {
        if (hasCreatedCampaign) {
            return connectPayoutAccount
                ? 'Copy recent campaign link'
                : 'Publish your campaign first';
        } else {
            return 'Create a campaign first';
        }
    }, [hasCreatedCampaign, connectPayoutAccount]);

    return (
        <RequiredItem.Root>
            <RequiredItem.TextContent className="gap-6 sm:gap-0">
                <div className="flex w-full flex-col items-start gap-3">
                    <RequiredItem.RequiredItemDuration>
                        As fast as you can!
                    </RequiredItem.RequiredItemDuration>
                    <div>
                        <RequiredItem.Title>Raise your first dollar</RequiredItem.Title>
                        <RequiredItem.Description>
                            Get your first donation! Share with supporters to build momentum—every
                            fundraising journey starts with that first dollar.
                        </RequiredItem.Description>
                    </div>
                </div>
                {raiseYourFirstDollar ? (
                    <RequiredItem.CompletedIndicator />
                ) : (
                    <div className="flex gap-2">
                        <RequiredItem.PrimaryButton
                            disabled={!hasCreatedCampaign || !connectPayoutAccount}
                            onClick={e => {
                                onClickCopy(e, recentCampaigns[0].url);
                                pushEvent(posthogEvents.DASHBOARD_CHECKLIST_ITEM_SELECTED, {
                                    checklistItem: 'raiseYourFirstDollar',
                                });
                            }}
                        >
                            <FontAwesomeIcon icon={faLink} />
                            {buttonText}
                        </RequiredItem.PrimaryButton>
                    </div>
                )}
            </RequiredItem.TextContent>
            <RequiredItem.Image
                desktopSrc="/images/graphics/homepage-checklist/raiseDollarDesktop.png"
                mobileSrc="/images/graphics/homepage-checklist/raiseDollarMobile.png"
            />
        </RequiredItem.Root>
    );
};

export { RequiredFirstDollar };
